import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { DateRange } from 'react-day-picker';
import type { SettlementFilter } from '@ip/shared-types';

/**
 * URL-persisted filters for the sales invoice list (SalesView), the same
 * mechanism useInvoiceFilters gives the cost list: filters live in query
 * params, so navigating to an invoice detail and back (or a page refresh)
 * restores them exactly.
 *
 * Deliberately a sibling of useInvoiceFilters rather than an extension of it:
 * the two lists share almost no filter vocabulary (settlement + year vs
 * mpp/status/source/dateType/tags), sales uses react-day-picker's DateRange
 * while the cost list uses a Dayjs tuple, and the lists live on different
 * routes so their params can never collide — a namespace/prefix would solve a
 * problem that doesn't exist while bloating both call sites.
 */

const DEFAULTS = {
  search: '',
  settlementFilter: 'all' as const,
  projectFilter: 'all',
  selectedTags: [] as string[],
  page: 1,
  pageSize: 20,
};

const VALID_SETTLEMENT = new Set<string>(['all', 'unpaid', 'overdue', 'partial', 'paid']);

export interface SalesFilters {
  /** Chart/summary year — part of the view state, restored the same way. */
  year: number;
  search: string;
  dateRange: DateRange | undefined;
  settlementFilter: SettlementFilter | 'all';
  /** Project id to filter by, or 'all'. */
  projectFilter: string;
  /** Sales-module tag ids to filter by (AND semantics, like the cost list). */
  selectedTags: string[];
  page: number;
  pageSize: number;
}

export interface SalesFilterActions {
  setYear: (value: number) => void;
  setSearch: (value: string) => void;
  setDateRange: (value: DateRange | undefined) => void;
  setSettlementFilter: (value: SettlementFilter | 'all') => void;
  setProjectFilter: (value: string) => void;
  setSelectedTags: (value: string[] | ((prev: string[]) => string[])) => void;
  setPage: (value: number) => void;
  setPageSize: (value: number) => void;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
}

export function useSalesFilters(): SalesFilters & SalesFilterActions {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<SalesFilters>(() => {
    const y = searchParams.get('y');
    const q = searchParams.get('q') || DEFAULTS.search;
    const df = searchParams.get('df');
    const dto = searchParams.get('dto');
    const st = searchParams.get('st');
    const proj = searchParams.get('proj');
    const tagsRaw = searchParams.get('t');
    const p = searchParams.get('p');
    const ps = searchParams.get('ps');

    let dateRange: DateRange | undefined;
    if (df || dto) {
      const from = df ? dayjs(df) : null;
      const to = dto ? dayjs(dto) : null;
      dateRange = {
        from: from?.isValid() ? from.toDate() : undefined,
        to: to?.isValid() ? to.toDate() : undefined,
      };
      if (!dateRange.from && !dateRange.to) dateRange = undefined;
    }

    const parsedYear = y ? parseInt(y, 10) : NaN;

    return {
      year: Number.isFinite(parsedYear) ? parsedYear : new Date().getFullYear(),
      search: q,
      dateRange,
      settlementFilter: (st && VALID_SETTLEMENT.has(st) ? st : DEFAULTS.settlementFilter) as
        | SettlementFilter
        | 'all',
      projectFilter: proj || DEFAULTS.projectFilter,
      selectedTags: tagsRaw ? tagsRaw.split(',').filter(Boolean) : DEFAULTS.selectedTags,
      page: p ? Math.max(1, parseInt(p, 10) || 1) : DEFAULTS.page,
      pageSize: ps ? Math.max(1, parseInt(ps, 10) || DEFAULTS.pageSize) : DEFAULTS.pageSize,
    };
  }, [searchParams]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Every filter setter clears the page param — a narrowed result set starts
  // at page 1 (replaces SalesView's old reset-to-page-1 effect, which would
  // wrongly fire on remount and destroy a restored page).
  const setYear = useCallback(
    (value: number) => {
      updateParams({ y: value === new Date().getFullYear() ? null : String(value) });
    },
    [updateParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      updateParams({ q: value || null, p: null });
    },
    [updateParams],
  );

  const setDateRange = useCallback(
    (value: DateRange | undefined) => {
      updateParams({
        df: value?.from ? dayjs(value.from).format('YYYY-MM-DD') : null,
        dto: value?.to ? dayjs(value.to).format('YYYY-MM-DD') : null,
        p: null,
      });
    },
    [updateParams],
  );

  const setSettlementFilter = useCallback(
    (value: SettlementFilter | 'all') => {
      updateParams({ st: value === DEFAULTS.settlementFilter ? null : value, p: null });
    },
    [updateParams],
  );

  const setProjectFilter = useCallback(
    (value: string) => {
      updateParams({ proj: value === DEFAULTS.projectFilter ? null : value, p: null });
    },
    [updateParams],
  );

  // Mirrors useInvoiceFilters' setSelectedTags: reads the current tag list
  // straight from the previous URLSearchParams (not the memoized `filters`)
  // so a toggle callback never closes over a stale selection.
  const setSelectedTags = useCallback(
    (value: string[] | ((prev: string[]) => string[])) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const currentTags = prev.get('t')?.split(',').filter(Boolean) || [];
          const newTags = typeof value === 'function' ? value(currentTags) : value;
          if (newTags.length === 0) {
            next.delete('t');
          } else {
            next.set('t', newTags.join(','));
          }
          next.delete('p');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (value: number) => {
      updateParams({ p: value === DEFAULTS.page ? null : String(value) });
    },
    [updateParams],
  );

  const setPageSize = useCallback(
    (value: number) => {
      updateParams({ ps: value === DEFAULTS.pageSize ? null : String(value), p: null });
    },
    [updateParams],
  );

  // Clears every filter but keeps the year (a view-level selector, not a
  // "filter" in the cost-list sense) and page size, mirroring
  // useInvoiceFilters.clearAllFilters.
  const clearAllFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();
        const y = prev.get('y');
        const ps = prev.get('ps');
        if (y) next.set('y', y);
        if (ps) next.set('ps', ps);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== DEFAULTS.search ||
      filters.dateRange !== undefined ||
      filters.settlementFilter !== DEFAULTS.settlementFilter ||
      filters.projectFilter !== DEFAULTS.projectFilter ||
      filters.selectedTags.length > 0
    );
  }, [filters]);

  return {
    ...filters,
    setYear,
    setSearch,
    setDateRange,
    setSettlementFilter,
    setProjectFilter,
    setSelectedTags,
    setPage,
    setPageSize,
    hasActiveFilters,
    clearAllFilters,
  };
}
